import { parseConfig } from '../config/parseConfig';
import { Vec3 } from '../math/Vec3';
import type { SimulationSnapshot } from './types';

export function measurementTemplate(snapshot: SimulationSnapshot): object {
  return {
    schemaVersion: 1,
    units: 'metres',
    config: snapshot.config,
    measurements: snapshot.voxels.map(voxel => ({
      emitterId: voxel.emitterId, voxelId: voxel.id,
      meshId: voxel.meshId, filamentId: voxel.filamentId,
      simulatedPosition: voxel.position,
      status: 'UNMEASURED', measuredPosition: null,
    })),
  };
}

export function compareMeasurements(input: unknown, snapshot: SimulationSnapshot): {
  measured: number; failed: number; unmeasured: number; rms: number | null; maximum: number | null;
} {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Registro de mediciones inválido.');
  const data = input as Record<string, unknown>;
  if (data.schemaVersion !== 1 || data.units !== 'metres') throw new Error('Mediciones: se requieren schemaVersion 1 y units metres.');
  if (!data.config) throw new Error('Falta la configuración del banco medido.');
  const config = parseConfig(data.config);
  for (const key of Object.keys(snapshot.config) as Array<keyof typeof config>) {
    if (JSON.stringify(config[key]) !== JSON.stringify(snapshot.config[key])) {
      throw new Error(`El registro pertenece a otra configuración (${key}). Carga primero su configuración.`);
    }
  }
  if (!Array.isArray(data.measurements) || data.measurements.length > snapshot.voxels.length) throw new Error('Lista de mediciones inválida.');
  const seen = new Set<number>();
  const voxels = new Map(snapshot.voxels.map(voxel => [voxel.id, voxel]));
  const errors: number[] = [];
  let failed = 0;
  for (const entry of data.measurements) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('Fila de medición inválida.');
    const voxel = voxels.get(entry.voxelId);
    if (!voxel || entry.emitterId !== voxel.emitterId || entry.meshId !== voxel.meshId || entry.filamentId !== voxel.filamentId || seen.has(voxel.id)) {
      throw new Error('Identificador desconocido, duplicado o no correspondiente al catálogo.');
    }
    seen.add(voxel.id);
    if (!['UNMEASURED', 'OK', 'MISS', 'BLOCKED', 'UNSTABLE', 'NOT_VISIBLE'].includes(entry.status)) throw new Error('Estado de medición desconocido.');
    if (entry.status !== 'OK') {
      if (entry.measuredPosition !== null) throw new Error('Solo las filas OK llevan measuredPosition; las demás usan null.');
      if (entry.status !== 'UNMEASURED') failed++;
      continue;
    }
    const point = entry.measuredPosition;
    if (!point || !['x', 'y', 'z'].every(axis => typeof point[axis] === 'number' && Number.isFinite(point[axis]) && Math.abs(point[axis]) <= 10)) {
      throw new Error('Una fila OK requiere coordenadas medidas finitas en metros (±10 m).');
    }
    errors.push(voxel.position.distanceTo(Vec3.from(point)));
  }
  return {
    measured: errors.length,
    failed,
    unmeasured: snapshot.voxels.length - errors.length - failed,
    rms: errors.length ? Math.sqrt(errors.reduce((sum, error) => sum + error * error, 0) / errors.length) : null,
    maximum: errors.length ? Math.max(...errors) : null,
  };
}
